/**
 * Die Zahlungen der Kennzahlen — Ladesteuerung.
 *
 * Die Belegliste braucht die Zahlungen NICHT: die Spalte "Offen" rechnet allein
 * aus den Summen der Belegzeile (openAmountCents in invoiceLogic). Nur zwei
 * Auswertungen haengen an ihnen — der durchschnittliche Zahlungseingang (DSO)
 * in der Kennzahlenleiste und die Zahlungsmoral je Kunde. Deshalb wird der
 * Abruf NEBEN das Laden der Liste gestellt: die Belege erscheinen, sobald sie
 * da sind, und die beiden Auswertungen zeigen so lange "wird geladen" statt
 * einer Zahl, die ohne Zahlungen falsch waere.
 *
 * Die Steuerung liegt hier und nicht in der Ansicht, aus denselben zwei
 * Gruenden wie bei pickerStockLogic:
 *
 * 1. Der Abruf haengt an einer LISTE VON BELEG-IDS, und die wechselt bei jedem
 *    Neuladen. load() hat viele Ausloeser — Rueckkehr aus dem Editor,
 *    Aktualisieren, Rechnungslauf, Kontoauszug-Import, Refresh aus der
 *    Buchhaltung. Zwei davon kurz hintereinander, und die Antworten koennen in
 *    umgekehrter Reihenfolge eintreffen: der aeltere Lauf schriebe dann seine
 *    Zahlungen ueber die des neueren. Auf dem Bildschirm faellt das nicht auf —
 *    es sind Zahlungen, nur eben die von vorhin. Welche Antwort einspielen
 *    darf, entscheidet deshalb allein die laufende Nummer der Anforderung.
 * 2. Ohne React ist genau dieser Fall pruefbar; im Projekt gibt es keine
 *    DOM-Testumgebung, mit der er sich an der Ansicht zeigen liesse.
 */

/** 'loading' heisst: die Kennzahlen duerfen noch keine Zahl behaupten. */
export type PaymentsLoad = 'idle' | 'loading' | 'failed';

export const PAYMENTS_LOAD_ERROR =
    'Die Zahlungen konnten nicht geladen werden. Die Kennzahlen unten rechnen ohne sie.';

export interface PaymentsStreamPorts<P> {
    /** Holt die Zahlungen zu genau diesen Belegen. */
    fetchFor: (invoiceIds: string[]) => Promise<P[]>;
    setPayments: (payments: P[]) => void;
    setLoad: (load: PaymentsLoad) => void;
    /**
     * Meldung an die Ansicht; null nimmt die vorige zurueck. `cause` ist der
     * urspruengliche Fehler — die Ansicht protokolliert ihn, die Meldung allein
     * sagt nur DASS etwas fehlt.
     */
    reportError: (message: string | null, cause?: unknown) => void;
}

export interface PaymentsStream {
    /** Neue Belegliste geladen: die Zahlungen dazu holen. */
    load: (invoiceIds: string[]) => void;
    /**
     * Ergebnisse werden nicht mehr gebraucht (die Ansicht verschwindet). Ein
     * laufender Abruf laeuft zu Ende, sein Ergebnis wird aber verworfen — er
     * darf nicht in Setter einer nicht mehr vorhandenen Ansicht schreiben.
     */
    cancel: () => void;
}

const messageOf = (error: unknown): string =>
    error instanceof Error && error.message
        ? `Die Zahlungen konnten nicht geladen werden (${error.message}). Die Kennzahlen unten rechnen ohne sie.`
        : PAYMENTS_LOAD_ERROR;

export const createPaymentsStream = <P>(ports: PaymentsStreamPorts<P>): PaymentsStream => {
    /** Laufende Nummer der Anforderung; nur die juengste darf einspielen. */
    let request = 0;

    return {
        load: (invoiceIds) => {
            const mine = ++request;
            ports.setLoad('loading');
            // Die Meldung des vorigen Laufs sofort wegnehmen: sie beschreibt
            // einen Stand, der gerade neu geholt wird. Bliebe sie stehen,
            // warnte das Banner vor fehlenden Zahlen, waehrend darunter schon
            // die neuen stuenden.
            ports.reportError(null);
            void (async () => {
                try {
                    const payments = await ports.fetchFor(invoiceIds);
                    if (mine !== request) return;
                    ports.setPayments(payments);
                    ports.setLoad('idle');
                } catch (e) {
                    if (mine !== request) return;
                    // Bewusst KEIN Abbruch der Ansicht: die Belege sind da und
                    // bleiben sichtbar. Ohne Zahlungen fehlen nur DSO und
                    // Zahlungsmoral — und weil das Banner darauf hinweist,
                    // duerfen die uebrigen Kennzahlen weiter rechnen.
                    ports.setPayments([]);
                    ports.setLoad('failed');
                    ports.reportError(messageOf(e), e);
                }
            })();
        },
        // Nur die Nummer hochzaehlen, keine Setter: der Abbruch kommt vom
        // Verlassen der Ansicht, und dort ist jeder Setter ins Leere gerichtet.
        cancel: () => {
            request += 1;
        },
    };
};
